import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'tea-component';
import CardLoading from '@op/components/CardLoading';
import moment from 'moment';
import './styles/markdown-preview.scss';
import './styles/index.scss';
import {
  handleContentScroll,
  handleNavigatorScroll,
} from '@/pages/opinion/utils';
import CardSlot from '@/pages/opinion/components/CardSlot';
import {
  HeadingsNavigator,
  DownloadButton,
  EmptyBody,
} from './components/SmallComponents';
import { downloadHtmlToFile } from '@op/utils/file';
import GptFilter from './components/GptFilter';
import TipsModal from './components/tips-modal';
import { useGptData } from './hooks';
import useReportLanguage from '../hooks/useReportLanguage';
import { useLocation } from 'umi';

export default function Gpt({
  gameInfo,
  wholeGameInfo,
  contentRef,
  scrolling,
  reportId,
  reportType = 1,
}: any) {
  const [tipsVisible, setTipsVisible] = useState(false);
  const location = useLocation();
  const defaultParams = useMemo(() => {
    return location?.query || {};
  }, []);

  const {
    time,
    setTime,
    empty,
    mdFileUrl,
    renderContentInfo,
    loading,
    hasDataTime,
    languageValue,
    setLanguageValue,
    setHasDataTime,
    navigator,
    setNavigator,
  } = useGptData({
    gameInfo,
    setTipsVisible,
    reportId,
    reportType,
    defaultParams,
  });

  const markdownRef = useRef<HTMLDivElement | null>(null);
  const [rendering, setRendering] = useState(false);
  // defaultParams.language这里还是用上次链接的语言
  const { options, loading: optionsLoading } = useReportLanguage({
    gameInfo,
    setLanguage: setLanguageValue,
    type: reportType,
    kind: reportId,
  });

  useEffect(() => {
    if (scrolling) {
      setNavigator(
        handleContentScroll(
          renderContentInfo.defaultNavigator,
          renderContentInfo.headings.map((item) => item.slug),
          contentRef,
          10,
        ),
      );
    }
  }, [scrolling]);

  const download = () => {
    const gameName = wholeGameInfo?.entity_name || '';
    const fileName = `Opinions_${gameName}_GPT_Auto_Reports_${moment(
      time[0],
    ).format('YYYYMMDD')}`;
    downloadHtmlToFile({
      fileName,
      ref: markdownRef,
      url: mdFileUrl,
      catchCallback: () => setRendering(false),
      finallyCallback: () => setRendering(false),
      thenCallback: () => setRendering(false),
    });
  };

  return (
    <>
      <TipsModal
        value={tipsVisible}
        setValue={setTipsVisible}
        tips={{
          en: (
            <>
              In order to obtain a full data report for the day, the Opinions
              system generates a GPT report using T-1's rules.
              <br />
              The date you selected includes today ({moment().format('MMDD')}
              ), it is recommended to change the end date to yesterday (
              {moment().subtract(1, 'days').format('MMDD')}), or please come
              tomorrow to apply for the {moment().format('MMDD')} report.
            </>
          ),
          zh: (
            <>
              为了获得当天的完整数据报告，舆情系统使用 T-1 的规则生成GPT报告。
              <br />
              您选择的日期包含今天（{moment().format('MMDD')}
              ），建议结束日期切换为昨天（
              {moment().subtract(1, 'days').format('MMDD')}），或请明日来申请
              {moment().format('MMDD')}报告。
            </>
          ),
        }}
      ></TipsModal>
      <GptFilter
        time={time}
        options={options}
        setTime={setTime}
        languageValue={languageValue}
        setLanguageValue={setLanguageValue}
        gameInfo={gameInfo}
      ></GptFilter>
      <CardLoading loading={loading || optionsLoading}>
        <CardSlot>
          <Row>
            <Col span={24}>
              <EmptyBody
                hasDataTime={hasDataTime}
                handleGotoHasMarkdown={() => {
                  setTime([hasDataTime.start_time, hasDataTime.end_time]);
                  setHasDataTime({ start_time: '', end_time: '' });
                }}
                empty={empty}
              >
                <>
                  {/* 禁用时加 disabled true */}
                  <DownloadButton
                    onClick={() => {
                      console.log('click');
                      setRendering(true);
                      download();
                    }}
                    rendering={rendering}
                    renderText={renderContentInfo.html}
                    markdownRef={markdownRef}
                  ></DownloadButton>

                  <HeadingsNavigator
                    list={renderContentInfo.headings}
                    navigator={navigator}
                    wholeGameInfo={wholeGameInfo}
                    contentRef={contentRef}
                    handleNavigatorScroll={handleNavigatorScroll}
                  ></HeadingsNavigator>
                </>
              </EmptyBody>
            </Col>
          </Row>
        </CardSlot>
      </CardLoading>
    </>
  );
}
